import type { V1PlanInfo } from "schemas/billing";
import type { BillingInterval } from "schemas/generated/billing";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { formatPrice } from "replo-utils/lib/math";
import { pluralize } from "replo-utils/lib/pluralize";
import {
  v1AdditionalPublishedElementBundlePricePerMonth,
  v1AdditionalPublishedElementBundleSize,
  v1AdditionalShopPricePerMonth,
} from "schemas/billing";

type V1PlanPriceVariant = "base" | "header";

export const getV1PlanPricePerMonthIncludingAllProducts = ({
  plan,
  billingInterval,
  additionalShops,
  additionalPublishedElementBundles,
}: {
  plan: V1PlanInfo;
  billingInterval: BillingInterval;
  additionalShops: number;
  additionalPublishedElementBundles: number;
}) => {
  const isYearly = billingInterval === "yearly";

  const pricePerMonthForAdditionalShops =
    additionalShops * v1AdditionalShopPricePerMonth;

  const pricePerMonthForAdditionalPublishedElementBundles =
    additionalPublishedElementBundles *
    v1AdditionalPublishedElementBundlePricePerMonth;

  const monthlyPriceForAllProducts =
    plan.monthlyPrice +
    pricePerMonthForAdditionalShops +
    pricePerMonthForAdditionalPublishedElementBundles;

  const yearlyPricePerMonthForAllProducts =
    plan.yearlyPricePerMonth +
    pricePerMonthForAdditionalShops +
    pricePerMonthForAdditionalPublishedElementBundles;

  const priceToDisplay = isYearly
    ? yearlyPricePerMonthForAllProducts
    : monthlyPriceForAllProducts;

  return priceToDisplay;
};

export const V1PlanPricePerMonthIncludingAllProducts = ({
  plan,
  billingInterval,
  additionalShops,
  additionalPublishedElementBundles,
  variant,
}: {
  plan: V1PlanInfo;
  billingInterval: BillingInterval;
  additionalShops: number;
  additionalPublishedElementBundles: number;
  variant: V1PlanPriceVariant;
}) => {
  const priceToDisplay = getV1PlanPricePerMonthIncludingAllProducts({
    plan,
    billingInterval,
    additionalShops,
    additionalPublishedElementBundles,
  });

  const formattedPrice = formatPrice(priceToDisplay);

  const variantClassName: Record<V1PlanPriceVariant, string> = {
    base: "text-lg font-medium",
    header: "text-3xl font-semibold",
  };

  const suffixClassName: Record<V1PlanPriceVariant, string> = {
    base: "text-lg font-medium",
    header: "text-base",
  };

  return (
    <span className={twMerge(variantClassName[variant])}>
      {formattedPrice}
      <span className={twMerge("font-normal", suffixClassName[variant])}>
        /month
      </span>
    </span>
  );
};

export const V1PlanPricePerMonthBreakdownByProducts = ({
  plan,
  billingInterval,
  additionalShops,
  additionalPublishedElementBundles,
  className,
}: {
  plan: V1PlanInfo;
  billingInterval: BillingInterval;
  additionalShops: number;
  additionalPublishedElementBundles: number;
  className?: string;
}) => {
  const isYearly = billingInterval === "yearly";

  const corePlanPricePerMonth = isYearly
    ? plan.yearlyPricePerMonth
    : plan.monthlyPrice;

  const formattedCorePlanPricePerMonth = formatPrice(corePlanPricePerMonth);

  const additionalShopsPricePerMonth =
    additionalShops * v1AdditionalShopPricePerMonth;

  const formattedAdditionalShopsPricePerMonth = formatPrice(
    additionalShopsPricePerMonth,
  );

  const additionalPublishedElementBundlesPricePerMonth =
    additionalPublishedElementBundles *
    v1AdditionalPublishedElementBundlePricePerMonth;

  const formattedAdditionalPublishedElementBundlesPricePerMonth = formatPrice(
    additionalPublishedElementBundlesPricePerMonth,
  );

  const totalAdditionalPublishedElements =
    additionalPublishedElementBundles * v1AdditionalPublishedElementBundleSize;

  return (
    <div className={twMerge("text-base", className)}>
      <div>
        <span className="font-bold">Replo {plan.displayName}</span> plan -{" "}
        <span className="font-bold">{formattedCorePlanPricePerMonth}</span>
      </div>
      {additionalShops > 0 && (
        <div>
          <span className="font-bold">{additionalShops}</span> additional
          Shopify{" "}
          {pluralize({
            singular: "integration",
            plural: "integrations",
            count: additionalShops,
          })}{" "}
          -{" "}
          <span className="font-bold">
            {formattedAdditionalShopsPricePerMonth}
          </span>
        </div>
      )}
      {additionalPublishedElementBundles > 0 && (
        <div>
          <span className="font-bold">{totalAdditionalPublishedElements}</span>{" "}
          additional published items -{" "}
          <span className="font-bold">
            {formattedAdditionalPublishedElementBundlesPricePerMonth}
          </span>
        </div>
      )}
    </div>
  );
};
